import React, { useState, useEffect } from 'react';
import { 
  Tv, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  ChevronRight,
  Filter,
  DollarSign,
  Wallet,
  ArrowLeft
} from 'lucide-react';
import useBillsStore from '../../../store/billsStore';
import useWalletStore from '../../../store/walletStore';
import { toast } from 'react-hot-toast';

const TVTab = () => {
  const [step, setStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [smartcardNumber, setSmartcardNumber] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [tvPackages, setTvPackages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isValidating, setIsValidating] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  
  const { 
    tvProviders, 
    recentSmartcards, 
    validateTV, 
    getTVPackages, 
    purchaseTV,
    getTVProviders, 
    isLoading 
  } = useBillsStore();
  
  const { wallet } = useWalletStore();

  useEffect(() => {
    getTVProviders(); 
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      loadPackages(selectedProvider.id);
    }
  }, [selectedProvider]);

  const loadPackages = async (serviceId) => {
    try {
      const packages = await getTVPackages(serviceId);
      setTvPackages(packages || []);
    } catch (error) {
      console.error('Failed to load TV packages:', error);
      setTvPackages([]);
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setStep(2);
  };

  const handleValidateSmartcard = async () => {
    if (!smartcardNumber || smartcardNumber.length < 10) {
      toast.error('Please enter a valid smartcard number');
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateTV({
        serviceId: selectedProvider.id,
        smartcardNumber,
      });

      setValidationResult(result);
      
      if (result.success && result.data.verified) {
        setStep(3);
        toast.success(`Smartcard validated! Customer: ${result.data.customerName}`);
      }
    } catch (error) {
      console.error('Smartcard validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setStep(4);
  };

  const handlePurchase = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    if (wallet?.balance < selectedPackage.amount) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      const result = await purchaseTV({
        serviceId: selectedProvider.id,
        smartcardNumber,
        packageCode: selectedPackage.code,
        amount: selectedPackage.amount,
      });

      setPurchaseResult(result);
      
      if (result.success) {
        setStep(5);
      }
    } catch (error) {
      console.error('TV subscription failed:', error);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getProviderColor = (provider) => {
    const colors = {
      'DSTV': 'bg-red-50 text-red-700 border-red-200',
      'GOTV': 'bg-blue-50 text-blue-700 border-blue-200',
      'STARTIMES': 'bg-orange-50 text-orange-700 border-orange-200',
      'SHOWMAX': 'bg-purple-50 text-purple-700 border-purple-200',
    };
    return colors[provider] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const filteredPackages = tvPackages.filter(pkg => {
    if (filter === 'all') return true;
    if (filter === 'cheap') return pkg.amount <= 5000;
    if (filter === 'popular') return pkg.name.toLowerCase().includes('compact') || pkg.name.toLowerCase().includes('yanga');
    if (filter === 'premium') return pkg.amount > 10000;
    return true;
  });

  const renderStep1 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h3 className="text-sm font-semibold text-gray-800">Select TV Provider</h3>
        </div>
        <p className="text-xs text-gray-500">Choose your TV subscription provider</p>
      </div>

      {/* Recent Smartcards */}
      {recentSmartcards.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-600 mb-2">Recent Smartcards</h4>
          <div className="flex flex-wrap gap-2">
            {recentSmartcards.map((smartcard) => (
              <button
                key={smartcard}
                onClick={() => setSmartcardNumber(smartcard)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#E8ECF5] shadow-neomorph-inset hover:shadow-neomorph transition-all text-xs font-medium text-gray-700"
              >
                <Tv className="h-3.5 w-3.5 mr-1.5" />
                <span className="truncate max-w-[100px]">{smartcard}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Providers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tvProviders.map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleProviderSelect(provider)}
            className="p-3 sm:p-4 rounded-xl bg-white shadow-neomorph hover:shadow-neomorph-hover border border-gray-100 text-left transition-all"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${
                provider.name === 'DSTV' ? 'from-red-500 to-red-600' :
                provider.name === 'GOTV' ? 'from-blue-500 to-blue-600' :
                provider.name === 'STARTIMES' ? 'from-orange-500 to-orange-600' :
                'from-purple-500 to-purple-600'
              } shadow-md flex-shrink-0`}>
                <Tv className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-gray-800 truncate">{provider.name}</h4>
                <p className="text-xs text-gray-500 truncate">{provider.code}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center mb-1">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Enter Smartcard Details</h3>
          </div>
          <p className="text-xs text-gray-500">Provider: {selectedProvider?.name}</p>
        </div>
        <button
          onClick={() => {
            setStep(1);
            setSelectedProvider(null);
            setValidationResult(null);
          }}
          className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset w-full sm:w-auto"
        >
          Change Provider
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="smartcardNumber" className="block text-xs font-medium text-gray-600 mb-1.5">
            Smartcard Number
          </label>
          <input
            id="smartcardNumber"
            type="text"
            value={smartcardNumber}
            onChange={(e) => setSmartcardNumber(e.target.value.replace(/\D/g, ''))}
            className="block w-full px-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            placeholder="Enter your smartcard number"
            maxLength={20}
          />
          <p className="mt-1.5 text-xs text-gray-500">Enter your 10-20 digit smartcard number</p>
        </div>

        {validationResult && (
          <div className={`rounded-xl p-4 ${
            validationResult.success && validationResult.data.verified
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          } shadow-neomorph-inset`}>
            <div className="flex items-start">
              <div className={`p-1.5 rounded-md ${
                validationResult.success && validationResult.data.verified
                  ? 'bg-green-100'
                  : 'bg-red-100'
              } shadow-neomorph-inset mr-3 flex-shrink-0`}>
                {validationResult.success && validationResult.data.verified ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className={`text-xs font-medium ${
                  validationResult.success && validationResult.data.verified
                    ? 'text-green-800'
                    : 'text-red-800'
                } mb-1`}>
                  {validationResult.success && validationResult.data.verified
                    ? 'Smartcard Verified'
                    : 'Verification Failed'}
                </h4>
                <p className={`text-xs ${
                  validationResult.success && validationResult.data.verified
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {validationResult.message}
                </p>
                {validationResult.success && validationResult.data.verified && (
                  <p className="text-xs font-medium text-green-800 mt-2 bg-white rounded-lg px-2 py-1 shadow-neomorph-inset break-words">
                    Customer: {validationResult.data.customerName}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => setStep(1)}
            className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
          >
            Back
          </button>
          <button
            onClick={handleValidateSmartcard}
            disabled={isValidating || !smartcardNumber || smartcardNumber.length < 10}
            className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isValidating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              'Validate Smartcard'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center mb-1">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Select Package</h3>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {selectedProvider?.name} - {smartcardNumber}
          </p>
          {validationResult?.data?.customerName && (
            <p className="text-xs text-gray-400 truncate">{validationResult.data.customerName}</p>
          )}
        </div>
        <button
          onClick={() => setStep(2)}
          className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset flex items-center justify-center w-full sm:w-auto"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Back
        </button>
      </div>

      {tvPackages.length > 0 ? (
        <>
          {/* Package Filters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">Filter Packages</label>
              <Filter className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'cheap', label: 'Under ₦5k' },
                { id: 'popular', label: 'Popular' },
                { id: 'premium', label: 'Premium' },
              ].map((filterOption) => (
                <button
                  key={filterOption.id}
                  type="button"
                  onClick={() => setFilter(filterOption.id)}
                  className={`
                    px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
                    ${filter === filterOption.id
                      ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-neomorph-inset'
                      : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                    }
                  `}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
            {filteredPackages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => handlePackageSelect(pkg)}
                className={`
                  p-3 sm:p-4 rounded-xl text-left transition-all border
                  ${selectedPackage?.id === pkg.id
                    ? 'border-primary-500 bg-primary-50 shadow-neomorph-inset'
                    : 'border-gray-200 bg-white shadow-neomorph hover:shadow-neomorph-hover'
                  }
                `}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-800 break-words">{pkg.name}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-lg border ${getProviderColor(selectedProvider?.name)} w-fit`}>
                    {selectedProvider?.name}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {pkg.metadata?.variation_amount ? '' : 'Monthly subscription'}
                </p>
                <div className="text-base sm:text-lg font-bold text-primary-600">
                  {formatAmount(pkg.amount)}
                </div>
                {pkg.metadata?.fixedPrice === 'Yes' && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      Fixed Price
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {filteredPackages.length === 0 && (
            <div className="text-center py-8 bg-[#E8ECF5] rounded-xl shadow-neomorph-inset">
              <Tv className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">No packages found</p>
              <p className="text-xs text-gray-400 mt-1">Try changing your filter</p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 bg-[#E8ECF5] rounded-xl shadow-neomorph-inset">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm text-gray-600">Loading packages...</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={() => setStep(2)}
          className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
        >
          Back
        </button>
        <button
          onClick={() => setStep(4)}
          disabled={!selectedPackage}
          className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next: Review
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center mb-1">
            <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h3 className="text-sm font-semibold text-gray-800">Review Purchase</h3>
          </div>
          <p className="text-xs text-gray-500">Confirm your TV subscription details</p>
        </div>
        <button
          onClick={() => setStep(3)}
          className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset flex items-center justify-center w-full sm:w-auto"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Back
        </button>
      </div>

      {selectedPackage && (
        <>
          {/* Review Summary */}
          <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph-inset">
            <h4 className="text-xs font-medium text-gray-600 mb-3">Subscription Details</h4>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-xs text-gray-500">Provider</span>
                <span className="text-xs font-medium text-gray-800">{selectedProvider?.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-xs text-gray-500">Smartcard Number</span>
                <span className="text-xs font-medium text-gray-800">{smartcardNumber}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-xs text-gray-500">Package</span>
                <span className="text-xs font-medium text-gray-800 break-words">{selectedPackage.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-xs text-gray-500">Customer Name</span>
                <span className="text-xs text-gray-800 break-words">{validationResult?.data?.customerName || 'N/A'}</span>
              </div>
              <div className="border-t border-gray-200/50 pt-3 mt-2">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-xs font-medium text-gray-700">Total Cost</span>
                  <span className="text-base font-bold text-primary-600">
                    {formatAmount(selectedPackage.amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Balance Check */}
          <div className="bg-blue-50 rounded-xl p-4 shadow-neomorph-inset border border-blue-200/50">
            <div className="flex items-start">
              <div className="p-1.5 bg-blue-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
                <Wallet className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-medium text-blue-800 mb-2">Wallet Balance</h4>
                <div className="space-y-1">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
                    <span className="text-blue-700">Available Balance:</span>
                    <span className="font-semibold text-blue-800">{formatAmount(wallet?.balance)}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
                    <span className="text-blue-700">After Purchase:</span>
                    <span className={`font-semibold ${
                      wallet?.balance >= selectedPackage.amount
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {formatAmount(wallet?.balance - selectedPackage.amount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="bg-yellow-50 rounded-xl p-4 shadow-neomorph-inset border border-yellow-200/50">
            <div className="flex items-start">
              <div className="p-1.5 bg-yellow-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-medium text-yellow-800 mb-2">Important Information</h4>
                <ul className="text-xs text-yellow-700 space-y-1 list-disc pl-4 break-words">
                  <li>Subscription will be activated within 5 minutes</li>
                  <li>Ensure your decoder is connected and powered on</li>
                  <li>Keep this receipt for your records</li>
                  <li>Contact support if activation is delayed</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => setStep(3)}
              className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
            >
              Back
            </button>
            <button
              onClick={handlePurchase}
              disabled={isLoading || wallet?.balance < selectedPackage.amount}
              className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Subscription'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4 sm:space-y-5">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
          <CheckCircle className="h-8 w-8 text-white" />
        </div>
        <div className="flex items-center justify-center mb-1">
          <div className="w-1 h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
          <h3 className="text-sm font-semibold text-gray-800">Subscription Successful!</h3>
        </div>
        <p className="text-xs text-gray-500">Your TV subscription was activated successfully</p>
      </div>

      <div className="bg-[#E8ECF5] rounded-xl p-4 shadow-neomorph-inset">
        <h4 className="text-xs font-medium text-gray-600 mb-3">Transaction Details</h4>
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
            <span className="text-gray-500">Provider</span>
            <span className="font-medium text-gray-800">{selectedProvider?.name}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
            <span className="text-gray-500">Smartcard Number</span>
            <span className="font-medium text-gray-800">{smartcardNumber}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
            <span className="text-gray-500">Package</span>
            <span className="font-medium text-gray-800 break-words">{selectedPackage?.name}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
            <span className="text-gray-500">Amount</span>
            <span className="font-semibold text-primary-600">
              {formatAmount(selectedPackage?.amount)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between gap-1 text-xs">
            <span className="text-gray-500">Transaction ID</span>
            <span className="text-xs font-mono text-gray-600 bg-white rounded-lg px-2 py-1 shadow-neomorph-inset break-all">
              {purchaseResult?.data?.transaction?.id?.slice(0, 8)}...
            </span>
          </div>
        </div>
      </div>

      <div className="bg-green-50 rounded-xl p-4 shadow-neomorph-inset border border-green-200/50">
        <div className="flex items-start">
          <div className="p-1.5 bg-green-100 rounded-md shadow-neomorph-inset mr-3 flex-shrink-0">
            <CheckCircle className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-xs font-medium text-green-800 mb-2">What's Next?</h4>
            <ul className="text-xs text-green-700 space-y-1 list-disc pl-4 break-words">
              <li>Your subscription will be activated within 5 minutes</li>
              <li>Keep your decoder powered on and connected</li>
              <li>You will receive a confirmation SMS</li>
              <li>Contact support if you don't see changes on your decoder</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={() => {
            setStep(1);
            setSelectedProvider(null);
            setSmartcardNumber('');
            setSelectedPackage(null);
            setValidationResult(null);
            setPurchaseResult(null);
          }}
          className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
        >
          New Subscription
        </button>
        <button
          onClick={() => window.location.href = '/bills'}
          className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white"
        >
          View All Bills
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Progress Steps */}
      <div className="flex items-center justify-center overflow-x-auto px-2">
        <div className="flex items-center min-w-max">
          {[1, 2, 3, 4, 5].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold ${
                step >= stepNum 
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {stepNum}
              </div>
              {stepNum < 5 && (
                <div className={`w-4 sm:w-6 h-1 mx-0.5 sm:mx-1 rounded-full ${
                  step > stepNum ? 'bg-gradient-to-r from-primary-500 to-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-5 shadow-neomorph border border-gray-100">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default TVTab;